import { useState, useRef } from 'react';
import { useInView } from 'motion/react';
import { useQuery } from '@apollo/client';

import ComponentWrapper from '../../fragments/component-wrapper/ComponentWrapper';

import CardStaff from '../../fragments/card/CardStaff.jsx';
import CardNews from '../../fragments/card/CardNews.jsx';

import ComponentHeading from '../../fragments/heading/ComponentHeading.jsx';
import CardGridFilters, { getVisibleFilters } from './CardGridFilters.jsx';
import StaffDeepDive from '../../fragments/staff-deep-dive/StaffDeepDive.jsx';
import Pagination from '../../fragments/pagination/Pagination.jsx';

import { loadCardsQuery } from '../../utils/LoadCards.query.wp';
import {
  transformLoadCards,
  getTotalPages,
} from '../../utils/LoadCards.transform.wp';
import {
  buildCardVariables,
  CARD_GRID_PAGE_SIZE,
} from '../../utils/useLoadCards';
import { DEEP_DIVE_POST_TYPES, resolveOrderby } from './CardGrid.transform.wp';

const SCHOOL = {
  '@type': 'School',
  name: 'The Quaker School at Horsham',
  alternateName: 'TQS',
};

// schema.org ItemList of Person for people grids, so search engines can tie
// each name (and role) to the school explicitly.
function PeopleSchema({ cards }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: cards
      .filter((card) => card.heading)
      .map((card, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Person',
          name: card.heading,
          ...(card.subheading && { jobTitle: card.subheading }),
          ...(card.image?.sourceUrl && { image: card.image.sourceUrl }),
          worksFor: SCHOOL,
        },
      })),
  };

  return (
    <script
      type="application/ld+json"
      // `<` is escaped so a name can never close the script tag early.
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schema).replace(/</g, '\\u003c'),
      }}
    />
  );
}

const CARD_COMPONENTS = {
  news: CardNews,
  staff: CardStaff,
  'board-members': CardStaff,
  default: CardNews,
};

export default function CardGrid({ block, index }) {
  const { cards = [], sourcePostType = 'news', filters = [] } = block ?? {};
  const CardComponent =
    CARD_COMPONENTS[sourcePostType] || CARD_COMPONENTS.default;

  // Selected term slugs per taxonomy: { [taxonomyName]: string[] }.
  const [selected, setSelected] = useState({});

  // Current pagination page (resets to 1 whenever filters change).
  const [page, setPage] = useState(1);

  // Card whose deep-dive modal is open (full transformed card object).
  const [activeStaff, setActiveStaff] = useState(null);

  const hasDeepDive = DEEP_DIVE_POST_TYPES.includes(sourcePostType);

  // With no visible filter bar there's no sidebar eating horizontal space, so
  // the desktop grid widens to 4 columns (see CardGrid.scss `--wide`).
  const hasFilterBar = getVisibleFilters(filters).length > 0;

  const gridTopRef = useRef(null);

  // Drives the mobile filters trigger. It's fixed to the viewport (portalled
  // out to <body>), so it needs telling when to show itself — otherwise it
  // floats over every other block on the page. `once: false` so it fades back
  // out again once the grid scrolls past.
  const gridInView = useInView(gridTopRef, { once: false });

  const hasActiveFilters = Object.values(selected).some(
    (terms) => terms.length > 0
  );

  // Scroll the grid back into view, clearing the fixed nav.
  const scrollToGridTop = () => {
    const el = gridTopRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80;
    if (window.lenis) window.lenis.scrollTo(y);
    else window.scrollTo({ top: y, behavior: 'smooth' });
  };

  // Toggle a single term within a taxonomy (OR within a taxonomy).
  const toggleTerm = (taxonomy, slug) => {
    setPage(1);
    setSelected((prev) => {
      const current = prev[taxonomy] || [];
      const next = current.includes(slug)
        ? current.filter((s) => s !== slug)
        : [...current, slug];
      return { ...prev, [taxonomy]: next };
    });
    scrollToGridTop();
  };

  // "All" clears a taxonomy's selection.
  const selectAll = (taxonomy) => {
    setPage(1);
    setSelected((prev) => ({ ...prev, [taxonomy]: [] }));
    scrollToGridTop();
  };

  const clearAll = () => {
    setPage(1);
    setSelected({});
    scrollToGridTop();
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    scrollToGridTop();
  };

  // Only hit WordPress once a filter is active or the user paginates past the
  // pre-fetched first page from getStaticProps. People grids prefetch the
  // whole set (`prefetchedAll`), so unfiltered pagination is purely local.
  const orderby = resolveOrderby(sourcePostType);
  const prefetchedAll = Boolean(block?.prefetchedAll);
  const usePrefetched = !hasActiveFilters && (prefetchedAll || page === 1);

  const { data, previousData, loading } = useQuery(loadCardsQuery, {
    variables: buildCardVariables({
      sourcePostType,
      numPosts: CARD_GRID_PAGE_SIZE,
      selectedTaxonomies: selected,
      orderby,
      paged: page,
    }),
    skip: usePrefetched,
  });

  // The Apollo client is no-cache, so `data` is undefined while a page/filter
  // change is in flight — fall back to the previous result to avoid an empty
  // flash, then to the pre-fetched page-1 cards.
  const raw = data ?? previousData;

  // Slice the current page out of a fully-prefetched set.
  const pageStart = (page - 1) * CARD_GRID_PAGE_SIZE;
  const pageEnd = pageStart + CARD_GRID_PAGE_SIZE;
  const showFullSet = usePrefetched && prefetchedAll;

  const displayCards =
    usePrefetched || !raw
      ? showFullSet
        ? cards.slice(pageStart, pageEnd)
        : cards
      : transformLoadCards(raw.cardPosts?.posts || []);

  // Everyone not on the current page. Kept in the DOM (hidden) so the static
  // HTML names every person — that's what lets Google match "<name> TQS"
  // searches to this page. Rendered as a separate list rather than hidden
  // items inside the grid so the grid's :nth-child layout rules still count
  // only visible cards.
  const offPageCards = showFullSet
    ? [...cards.slice(0, pageStart), ...cards.slice(pageEnd)]
    : [];

  const totalPages =
    usePrefetched || !raw
      ? (block?.totalPages ?? 1)
      : getTotalPages(raw.cardPosts);

  // No cards were prefetched and no filters are active: nothing to filter or show.
  const isEmptyOnLoad = !hasActiveFilters && cards.length === 0;

  // Guard sits below every hook so the hook order is identical on every render.
  if (!block || !Array.isArray(block.cards)) return null;

  return (
    <ComponentWrapper
      className={`CardGrid CardGrid--${sourcePostType}${
        !hasFilterBar ? ' CardGrid--wide' : ''
      }`}
    >
      {isEmptyOnLoad ? (
        <div className="l-grid">
          <div className="l-grid__col l-grid__col--l-6-12 l-grid__col--l-push-3-12">
            <div className="CardGrid-emptyState">
              <h3 className="CardGrid-emptyHeading">
                No upcoming {sourcePostType} right now
              </h3>
              <p className="CardGrid-emptyDescription">
                There are no{' '}
                {sourcePostType == 'events'
                  ? 'upcoming events scheduled'
                  : 'news articles'}{' '}
                at the moment. Please check back soon for new opportunities to
                connect with The Quaker School at Horsham.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="l-grid">
          {block.heading && <ComponentHeading heading={block.heading} />}

          <div className="l-grid__col" ref={gridTopRef}>
            <div className="CardGrid-layout">
              <CardGridFilters
                filters={filters}
                isInView={gridInView}
                selected={selected}
                onToggleTerm={toggleTerm}
                onSelectAll={selectAll}
                onClearAll={clearAll}
              />

              <div
                className="CardGrid-gridWrapper"
                aria-busy={loading}
                data-loading={loading ? 'true' : undefined}
              >
                {displayCards.length === 0 ? (
                  <p className="CardGrid-empty">
                    No results match your filters.
                  </p>
                ) : (
                  <ul className="CardGrid-grid">
                    {displayCards.map((card, i) => (
                      <li className="CardGrid-item" key={card.databaseId ?? i}>
                        <CardComponent
                          card={card}
                          isEvent={sourcePostType === 'events'}
                          {...(!block?.theme && { colorIndex: i })}
                          onOpenDeepDive={
                            hasDeepDive ? setActiveStaff : undefined
                          }
                        />
                      </li>
                    ))}
                  </ul>
                )}

                {offPageCards.length > 0 && (
                  <ul className="CardGrid-offPage" hidden>
                    {offPageCards.map((card, i) => (
                      <li key={card.databaseId ?? `off-${i}`}>
                        {/* Image dropped: only the name/role text matters
                            here, and srcsets + blur data URIs for every
                            hidden card would bloat the HTML. */}
                        <CardComponent card={{ ...card, image: false }} />
                      </li>
                    ))}
                  </ul>
                )}

                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {hasDeepDive && prefetchedAll && cards.length > 0 && (
        <PeopleSchema cards={cards} />
      )}

      {hasDeepDive && (
        <StaffDeepDive
          card={activeStaff}
          postType={sourcePostType}
          onClose={() => setActiveStaff(null)}
        />
      )}
    </ComponentWrapper>
  );
}
